/** MIDI file parser and scheduled playback engine.
 *
 * Converts a raw MIDI file into a flat list of timed note events,
 * then plays them through the SynthController using setTimeout scheduling.
 * Supports looping and real-time tempo adjustment.
 */

import type { MidiData, MidiEvent } from "midi-file";
import { parseMidi } from "midi-file";

/** A single note event with an absolute time in seconds (at original tempo). */
export type ScheduledNote = {
	readonly time: number;
	readonly note: number;
	readonly velocity: number;
	readonly type: "noteOn" | "noteOff";
};

/** Metadata extracted from the MIDI file. */
export type MidiFileInfo = {
	readonly name: string;
	readonly trackCount: number;
	readonly durationSeconds: number;
	readonly noteCount: number;
	/** Original BPM detected from the MIDI file (or 120 default). */
	readonly originalBpm: number;
};

/** Playback options for the MIDI player. */
export type MidiPlaybackOptions = {
	/** Whether to loop playback. */
	readonly loop: boolean;
	/** Tempo multiplier (1.0 = original). Overridden by bpm if set. */
	readonly tempoScale: number;
};

/** Default playback options. */
export const DEFAULT_PLAYBACK_OPTIONS: MidiPlaybackOptions = {
	loop: false,
	tempoScale: 1.0,
} as const;

/** Parse a MIDI file ArrayBuffer into a sorted list of note events. */
export function parseMidiFile(
	buffer: ArrayBuffer,
	fileName: string,
): { readonly notes: readonly ScheduledNote[]; readonly info: MidiFileInfo } {
	const data: MidiData = parseMidi(new Uint8Array(buffer));
	const ticksPerBeat = data.header.ticksPerBeat ?? 480;

	const notes: ScheduledNote[] = [];
	let maxTime = 0;
	let detectedBpm = 120;

	for (const track of data.tracks) {
		let microsecondsPerBeat = 500_000; // 120 BPM default
		let absoluteTime = 0;
		let firstTempo = true;

		for (const event of track) {
			// Convert delta ticks to seconds using current tempo
			const deltaTicks = event.deltaTime;
			const deltaSeconds =
				(deltaTicks / ticksPerBeat) * (microsecondsPerBeat / 1_000_000);
			absoluteTime += deltaSeconds;

			if (event.type === "setTempo") {
				microsecondsPerBeat = (
					event as MidiEvent & { microsecondsPerBeat: number }
				).microsecondsPerBeat;
				if (firstTempo) {
					detectedBpm = Math.round(60_000_000 / microsecondsPerBeat);
					firstTempo = false;
				}
			}

			if (event.type === "noteOn") {
				const e = event as MidiEvent & {
					noteNumber: number;
					velocity: number;
				};
				if (e.velocity > 0) {
					notes.push({
						time: absoluteTime,
						note: e.noteNumber,
						velocity: e.velocity,
						type: "noteOn",
					});
				} else {
					// noteOn with velocity 0 is treated as noteOff
					notes.push({
						time: absoluteTime,
						note: e.noteNumber,
						velocity: 0,
						type: "noteOff",
					});
				}
			}

			if (event.type === "noteOff") {
				const e = event as MidiEvent & { noteNumber: number; velocity: number };
				notes.push({
					time: absoluteTime,
					note: e.noteNumber,
					velocity: e.velocity,
					type: "noteOff",
				});
			}

			if (absoluteTime > maxTime) maxTime = absoluteTime;
		}
	}

	// Sort by time, noteOff before noteOn at the same time
	notes.sort((a, b) => {
		if (a.time !== b.time) return a.time - b.time;
		if (a.type === "noteOff" && b.type === "noteOn") return -1;
		if (a.type === "noteOn" && b.type === "noteOff") return 1;
		return 0;
	});

	const noteOnCount = notes.filter((n) => n.type === "noteOn").length;

	return {
		notes,
		info: {
			name: fileName.replace(/\.midi?$/i, ""),
			trackCount: data.tracks.length,
			durationSeconds: maxTime,
			noteCount: noteOnCount,
			originalBpm: detectedBpm,
		},
	};
}

/** Callback interface for driving note events during playback. */
export type MidiPlayerCallbacks = {
	readonly noteOn: (note: number) => void;
	readonly noteOff: (note: number) => void;
	readonly onProgress: (time: number, duration: number) => void;
	readonly onFinished: () => void;
};

/** Controls for an active MIDI playback session. */
export type MidiPlaybackHandle = {
	readonly stop: () => void;
	readonly isPlaying: () => boolean;
	/** Update tempo scale while playing. */
	readonly setTempoScale: (scale: number) => void;
	/** Update loop setting while playing. */
	readonly setLoop: (loop: boolean) => void;
};

/** Schedule and play a list of MIDI note events with loop and tempo support. */
export function playMidiNotes(
	notes: readonly ScheduledNote[],
	callbacks: MidiPlayerCallbacks,
	options: MidiPlaybackOptions = DEFAULT_PLAYBACK_OPTIONS,
): MidiPlaybackHandle {
	let stopped = false;
	let tempoScale = options.tempoScale;
	let loop = options.loop;
	let timers: ReturnType<typeof setTimeout>[] = [];
	let progressInterval: ReturnType<typeof setInterval> | null = null;
	const lastNote = notes[notes.length - 1];
	const baseDuration = lastNote ? lastNote.time + 0.5 : 0;
	let loopStartTime = performance.now();

	/** Get effective duration at current tempo. */
	const getScaledDuration = () => baseDuration / tempoScale;

	/** Schedule one pass of all events from a given offset. */
	const schedulePass = () => {
		// Clear any existing timers
		for (const t of timers) clearTimeout(t);
		timers = [];
		loopStartTime = performance.now();

		const scaledDuration = getScaledDuration();

		for (const event of notes) {
			const delayMs = (event.time / tempoScale) * 1000;

			const timer = setTimeout(() => {
				if (stopped) return;
				if (event.type === "noteOn") {
					callbacks.noteOn(event.note);
				} else {
					callbacks.noteOff(event.note);
				}
			}, delayMs);

			timers.push(timer);
		}

		// Schedule end / loop restart
		const endTimer = setTimeout(() => {
			if (stopped) return;
			if (loop) {
				// Turn off all active notes before restarting
				const activeNotes = new Set<number>();
				for (const event of notes) {
					if (event.type === "noteOn") activeNotes.add(event.note);
					else activeNotes.delete(event.note);
				}
				for (const note of activeNotes) {
					callbacks.noteOff(note);
				}
				schedulePass();
			} else {
				if (progressInterval) clearInterval(progressInterval);
				callbacks.onFinished();
			}
		}, scaledDuration * 1000);
		timers.push(endTimer);
	};

	// Progress ticker
	progressInterval = setInterval(() => {
		if (stopped) return;
		const scaledDuration = getScaledDuration();
		const elapsed = (performance.now() - loopStartTime) / 1000;
		const displayTime = loop
			? elapsed % scaledDuration
			: Math.min(elapsed, scaledDuration);
		callbacks.onProgress(displayTime, scaledDuration);
	}, 100);

	// Start the first pass
	schedulePass();

	return {
		stop() {
			stopped = true;
			for (const t of timers) clearTimeout(t);
			if (progressInterval) clearInterval(progressInterval);
			// Send noteOff for all notes that may still be sounding
			const activeNotes = new Set<number>();
			for (const event of notes) {
				if (event.type === "noteOn") activeNotes.add(event.note);
				else activeNotes.delete(event.note);
			}
			// Release every note that could have been triggered
			const allNotes = new Set<number>();
			for (const event of notes) {
				allNotes.add(event.note);
			}
			for (const note of allNotes) {
				callbacks.noteOff(note);
			}
		},
		isPlaying() {
			return !stopped;
		},
		setTempoScale(newScale: number) {
			if (stopped) return;
			tempoScale = Math.max(0.1, Math.min(4.0, newScale));
			// Reschedule remaining events at new tempo
			schedulePass();
		},
		setLoop(newLoop: boolean) {
			loop = newLoop;
		},
	};
}
